import React from 'react';

/**
 * Utilitário centralizado para tratamento de erros
 * Padroniza as mensagens de erro e comportamentos em toda a aplicação
 */

export class ErrorHandler {
  /**
   * Processa erros de API e retorna mensagem amigável
   * @param {Error} error - O erro capturado
   * @param {string} defaultMessage - Mensagem padrão se não conseguir extrair do erro
   * @returns {string} Mensagem de erro formatada
   */
  static getErrorMessage(error, defaultMessage = 'Ocorreu um erro inesperado. Por favor, tente novamente.') {
    // Erro de resposta da API
    if (error.response) {
      const { status, data } = error.response;
      
      // Mensagens específicas por status HTTP
      switch (status) {
        case 400:
          return data?.error || data?.msg || 'Dados inválidos. Verifique as informações e tente novamente.';
        case 401:
          return 'Sua sessão expirou. Por favor, faça login novamente.';
        case 403:
          return 'Você não tem permissão para realizar esta ação.';
        case 404:
          return 'Recurso não encontrado.';
        case 405:
          return 'Operação não permitida. Entre em contato com o administrador.';
        case 409:
          return data?.error || data?.msg || 'Conflito de dados. Verifique se os dados já existem.';
        case 422:
          return data?.error || data?.msg || 'Dados inválidos. Verifique os campos obrigatórios.';
        case 429:
          return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
        case 500:
          return 'Erro interno do servidor. Nossa equipe foi notificada.';
        case 502:
        case 503:
        case 504:
          return 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
        default:
          return data?.error || data?.msg || `Erro ${status}: ${error.response.statusText}`;
      }
    }
    
    // Erro de rede/conexão
    if (error.request) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
    }
    
    // Erro de configuração ou outros
    if (error.message) {
      return error.message;
    }
    
    return defaultMessage;
  }

  /**
   * Trata erros de autenticação de forma consistente
   * @param {Error} error - O erro de autenticação
   * @param {Function} navigate - Função de navegação do React Router
   */
  static handleAuthError(error, navigate) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Limpar dados de autenticação
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirecionar para login
      navigate('/login', { 
        replace: true,
        state: { message: 'Sua sessão expirou. Por favor, faça login novamente.' }
      });
    }
  }

  /**
   * Registra erros para debugging e monitoramento
   * @param {Error} error - O erro a ser registrado
   * @param {string} context - Contexto onde o erro ocorreu
   */
  static logError(error, context = 'Unknown') {
    // Apenas fazer log em desenvolvimento ou se especificamente configurado
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_ERROR_LOGS === 'true') {
      console.group(`🚨 Error in ${context}`);
      console.error('Error object:', error);
      
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      console.error('Stack trace:', error.stack);
      console.groupEnd();
    }

    // Em produção, você pode enviar erros para um serviço de monitoramento
    // como Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // TODO: Integrar com serviço de monitoramento de erros
      // Exemplo: Sentry.captureException(error, { contexts: { errorContext: context } });
    }
  }

  /**
   * Valida se um token JWT está válido e não expirado
   * @param {string} token - O token JWT
   * @returns {boolean} True se válido, false caso contrário
   */
  static isTokenValid(token) {
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Verificar se o token não expirou
      return payload.exp > currentTime;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Token inválido:', error);
      }
      return false;
    }
  }
}

/**
 * Hook personalizado para tratamento de erros em componentes React
 * @param {number} clearDelay - Tempo em ms para limpar o erro automaticamente
 * @returns {object} Objeto com estado e funções para gerenciar erros
 */
export const useErrorHandler = (clearDelay = 5000) => {
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const handleError = React.useCallback((error, context = 'Component') => {
    ErrorHandler.logError(error, context);
    const message = ErrorHandler.getErrorMessage(error);
    setError(message);
    
    if (clearDelay > 0) {
      setTimeout(() => setError(''), clearDelay);
    }
  }, [clearDelay]);

  const clearError = React.useCallback(() => {
    setError('');
  }, []);

  const executeAsync = React.useCallback(async (asyncFn, context = 'Async Operation') => {
    try {
      setIsLoading(true);
      setError('');
      const result = await asyncFn();
      return result;
    } catch (error) {
      handleError(error, context);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  return {
    error,
    isLoading,
    handleError,
    clearError,
    executeAsync
  };
};

export default ErrorHandler; 